import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isAxiosError } from "axios";
import Layout from "../layout/Layout";
import { apiV2 } from "../../../api/axiosConfig";
import BasicText from "../../../components/form-v2/basic-text";
import { BlueButton } from "../../../components/ui/Buttons";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import { DepartmentSelectV2 } from "../../../lib/departments/DepartmentSelectV2";
import { Department } from "../../../types/userManagement";
import { useAuth } from "../../../context/user";
import { UserSelector } from "../../../components/paginated-selector/user-selector";
import ModalV2 from "../../../components/portal/ModalV2";

const NotificationGroupEdit: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const auth = useAuth();

    const [name, setName] = useState("");
    const [userIDs, setUserIDs] = useState<string[]>([]);
    const [clientID, setClientID] = useState<string>("");
    const [initialDepartmentID, setInitialDepartmentID] = useState<string | undefined>(undefined);
    const [department, setDepartment] = useState<Department | undefined>(undefined);
    const [showUsersModal, setShowUsersModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const departmentID = department?.id;
    const canSave = !!name && (!auth.isDepartmentAdmin || !!departmentID);

    useEffect(() => {
        apiV2.get(`notification-groups/${id}`)
            .then((res) => {
                setName(res.data.name);
                setUserIDs(res.data.userIDs ?? []);
                setClientID(res.data.clientID ?? "");
                if (res.data.departmentID) {
                    setInitialDepartmentID(res.data.departmentID);
                    setDepartment({ id: res.data.departmentID } as Department);
                }
            })
            .catch(() => setError("Failed to load notification group"))
            .finally(() => setLoading(false));
    }, [id]);

    const save = useCallback(async () => {
        setSaving(true);
        try {
            await apiV2.patch(`notification-groups/${id}`, { name, userIDs, departmentID });
            navigate("/management/notification-groups?message=Notification group updated");
        } catch (e) {
            if (isAxiosError(e) && e?.response?.data?.error) {
                setError(e.response.data.error);
            } else {
                setError("An error has occurred");
            }
            setSaving(false);
        }
    }, [id, name, userIDs, departmentID]);

    if (loading) {
        return (
            <Layout active="notification-groups">
                <div className="flex justify-center py-16">
                    <LoadingSpinner width={32} height={32} />
                </div>
            </Layout>
        );
    }

    return (
        <Layout active="notification-groups">
            <div className="flex flex-col gap-8">
                <div className="bg-white shadow-md p-4 w-1/2 flex flex-col gap-8">
                    <p className="text-md font-semibold">Edit Notification Group</p>
                    <BasicText value={name} onChange={setName} label="Name" />
                    {initialDepartmentID && (
                        <div className="flex flex-col">
                            <label className="mb-1">Department</label>
                            <DepartmentSelectV2 clientID={clientID} initialID={initialDepartmentID} isDisabled onChange={(d) => {
                                setUserIDs([]);
                                setDepartment(d);
                            }} />
                        </div>
                    )}
                    <div className="flex flex-col">
                        <label className="mb-1">Users</label>
                        <button
                            type="button"
                            onClick={() => setShowUsersModal(true)}
                            disabled={auth.isDepartmentAdmin && !departmentID}
                            className="h-10 w-full shadow-md rounded-md px-4 bg-white hover:bg-gray-50 flex items-center justify-between text-left disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className={userIDs.length === 0 ? "text-gray-400" : ""}>
                                {userIDs.length === 0
                                    ? "Select users"
                                    : `${userIDs.length} user${userIDs.length === 1 ? "" : "s"} selected`}
                            </span>
                            <span className="text-gray-400">▼</span>
                        </button>
                    </div>
                    {clientID && (
                        <ModalV2 open={showUsersModal} setOpen={setShowUsersModal} className="pt-10">
                            <UserSelector ids={userIDs} onChange={setUserIDs} clientID={clientID} departmentID={departmentID} />
                        </ModalV2>
                    )}
                    <BlueButton text="Save" disabled={!canSave} onClick={save} loading={saving} />
                    {error && <p className="text-center text-red">{error}</p>}
                </div>
            </div>
        </Layout>
    );
};

NotificationGroupEdit.displayName = "NotificationGroupEdit";

export default NotificationGroupEdit;
